import { defineComponent, h, ref, useAttrs } from 'vue'
import { VCheckboxBtn, VSelect, VListItem } from 'vuetify/components'
import {
  hasBooleanishAttr,
  normalizeLegacyMenuProps,
  resolveBgColor,
  resolveDensity,
  resolveVariant,
  stripLegacyInputAttrs,
  withLegacyDirtyClass
} from './legacyInputProps'

VSelect.compatConfig = {
  ...(VSelect.compatConfig || {}),
  COMPONENT_V_MODEL: false,
  INSTANCE_ATTRS_CLASS_STYLE: 'suppress-warning'
}

function getLegacyItemSelector(attrs = {}, camelName, kebabName, fallback) {
  return attrs[camelName] ?? attrs[kebabName] ?? fallback
}

function getItemProperty(item, selector, fallback) {
  if (item == null) return fallback

  let result
  if (typeof selector === 'function') {
    result = selector(item, fallback)
  } else if (Array.isArray(selector)) {
    result = selector.reduce(
      (value, key) => (value == null ? value : value[key]),
      item
    )
  } else if (typeof selector === 'string' && selector) {
    result = selector.split('.').reduce((value, key) => {
      return value == null ? value : value[key]
    }, item)
  }

  // Vuetify 3's default item-title/item-value key is 'title'/'value',
  // whereas Vuetify 2 defaulted to 'text'/'value'. Components that never
  // explicitly set item-text/item-title (relying on Vue 2's old default)
  // would otherwise resolve to undefined here and fall through to
  // Vuetify's own JSON-stringified-object display fallback.
  return result ?? fallback
}

function normalizeItem(item, attrs = {}) {
  if (item == null || typeof item !== 'object' || Array.isArray(item)) {
    return item
  }

  const titleSelector = getLegacyItemSelector(
    attrs,
    'itemTitle',
    'item-text',
    attrs.itemText ?? 'title'
  )
  const valueSelector = getLegacyItemSelector(
    attrs,
    'itemValue',
    'item-value',
    attrs.itemValue ?? 'value'
  )
  const disabledSelector = getLegacyItemSelector(
    attrs,
    'itemDisabled',
    'item-disabled'
  )
  const title = getItemProperty(
    item,
    titleSelector,
    item.title ?? item.text ?? item.label ?? item.value
  )
  const value = getItemProperty(
    item,
    valueSelector,
    item.value ?? item.key ?? item.id
  )
  const disabled =
    disabledSelector == null
      ? undefined
      : getItemProperty(item, disabledSelector, item.disabled)

  return {
    ...item,
    title,
    value,
    key: item.key ?? value ?? item.id,
    ...(disabled === undefined
      ? {}
      : {
          props: {
            ...(item.props || {}),
            disabled
          },
          disabled
        })
  }
}

function normalizeItems(items, attrs) {
  return Array.isArray(items)
    ? items.map((item) => normalizeItem(item, attrs))
    : items
}

function hasLegacyTextItems(items) {
  return (
    Array.isArray(items) &&
    items.some((item) => item && typeof item === 'object' && item.text != null)
  )
}

export function normalizeMenuProps(menuProps) {
  return normalizeLegacyMenuProps({ menuProps })
}

function normalizeModelValue(attrs) {
  return attrs.modelValue ?? attrs.value
}

function normalizeSearchValue(attrs) {
  return attrs.search ?? attrs.searchInput ?? attrs['search-input']
}

function stripLegacyAttrs(attrs) {
  const normalized = stripLegacyInputAttrs(attrs)
  const menuProps = normalizeLegacyMenuProps(attrs)

  if (menuProps) {
    normalized.menuProps = menuProps
  }

  delete normalized.value
  if (attrs['item-text'] != null && normalized.itemTitle == null) {
    normalized.itemTitle = attrs['item-text']
  }
  if (attrs.itemText != null && normalized.itemTitle == null) {
    normalized.itemTitle = attrs.itemText
  }
  delete normalized.itemText
  delete normalized['item-text']
  if (attrs['item-value'] != null && normalized.itemValue == null) {
    normalized.itemValue = attrs['item-value']
  }
  if (attrs.itemValue != null && normalized.itemValue == null) {
    normalized.itemValue = attrs.itemValue
  }
  delete normalized.itemValue
  delete normalized['item-value']
  if (
    (attrs['item-disabled'] != null || attrs.itemDisabled != null) &&
    normalized.itemProps == null
  ) {
    const disabledSelector = attrs.itemDisabled ?? attrs['item-disabled']
    normalized.itemProps = (item) => ({
      disabled: getItemProperty(item, disabledSelector, item?.disabled)
    })
  }
  delete normalized.itemDisabled
  delete normalized['item-disabled']
  delete normalized.searchInput
  delete normalized['search-input']
  delete normalized['menu-props']
  return normalized
}

function extractLegacyBindings(props = {}) {
  const attrs = {}
  const on = {}

  Object.entries(props).forEach(([key, value]) => {
    if (/^on[A-Z]/.test(key)) {
      on[key.slice(2).toLowerCase()] = value
    } else if (key !== 'title') {
      attrs[key] = value
    }
  })

  return { attrs, on }
}

function normalizeSlotItem(item) {
  const raw = item?.raw ?? item
  const value = getSlotItemValue(item)

  if (
    item &&
    typeof item === 'object' &&
    item.raw == null &&
    item.text == null &&
    item.title == null &&
    item.label == null &&
    value != null
  ) {
    return value
  }

  if (raw == null && value != null) {
    return value
  }

  if (raw && typeof raw !== 'object') {
    return raw
  }

  if (raw && typeof raw === 'object') {
    return {
      ...item,
      ...raw,
      raw,
      text: raw.text ?? raw.title ?? item?.title,
      value: raw.value ?? raw.key ?? item?.value ?? item?.key
    }
  }

  return raw
}

function resolveLegacyItemFromItems(items, value) {
  if (!Array.isArray(items)) return undefined

  return items.find((item) => valuesMatch(getSlotItemValue(item), value))
}

function getSlotItemValue(item) {
  if (item == null) return item
  if (typeof item !== 'object') return item
  return (
    item.value ??
    item.key ??
    item.raw?.value ??
    item.raw?.key ??
    item.raw ??
    item
  )
}

function valuesMatch(left, right) {
  if (left === right) return true
  if (left == null || right == null) return false
  return String(left) === String(right)
}

function toggleLegacyItemValue(modelValue, item) {
  const itemValue = getSlotItemValue(item)

  if (Array.isArray(modelValue)) {
    const alreadySelected = modelValue.some((value) =>
      valuesMatch(value, itemValue)
    )

    return alreadySelected
      ? modelValue.filter((value) => !valuesMatch(value, itemValue))
      : [...modelValue, itemValue]
  }

  return itemValue
}

function isMultipleSelect(attrs = {}) {
  return (
    attrs.multiple === '' ||
    attrs.multiple === true ||
    attrs.multiple === 'true'
  )
}

function resolveSelectedLegacyItem(items, modelValue) {
  const selectedValue = Array.isArray(modelValue) ? modelValue[0] : modelValue
  return normalizeSlotItem(resolveLegacyItemFromItems(items, selectedValue))
}

function renderLegacySelectionFallback(slots, items, modelValue) {
  const selectedItem = resolveSelectedLegacyItem(items, modelValue)
  if (selectedItem == null) return undefined

  if (!slots.selection) {
    const text =
      selectedItem.text ??
      selectedItem.title ??
      selectedItem.label ??
      selectedItem.value ??
      selectedItem

    return h('span', { class: 'v-select__selection text-no-wrap' }, text)
  }

  return slots.selection(
    normalizeSelectionScope(
      {
        item: selectedItem,
        index: 0
      },
      items,
      modelValue
    )
  )
}

function renderDefaultItemLabel(item) {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item)

  return item.text ?? item.title ?? item.label ?? item.value ?? item.key ?? ''
}

export function isLegacyItemSelected(modelValue, item) {
  const itemValue = getSlotItemValue(item)

  // With `return-object`, the model array holds full item objects rather
  // than their `item-value` scalars, so `value` here can be an object
  // while `itemValue` is always the scalar extracted from `item`.
  // Comparing them directly (or via String()) never matches - run `value`
  // through the same extraction so both sides compare like-for-like. This
  // only affects the checkbox/active display computed here; actual
  // selection already works via Vuetify's own native click handler.
  if (Array.isArray(modelValue)) {
    return modelValue.some((value) =>
      valuesMatch(getSlotItemValue(value), itemValue)
    )
  }

  return valuesMatch(getSlotItemValue(modelValue), itemValue)
}

export function normalizeItemScope(scope = {}, modelValue, onLegacySelect) {
  const { attrs: legacyAttrs, on } = extractLegacyBindings(scope?.props || {})
  const item = normalizeSlotItem(scope?.item)
  const modelSelected = isLegacyItemSelected(modelValue, item)
  const active =
    modelSelected ||
    Boolean(
      scope?.props?.active ??
      scope?.props?.selected ??
      scope?.isSelected ??
      scope?.selected ??
      false
    )

  const legacyOn = { ...on }
  const originalClick = legacyOn.click

  if (typeof onLegacySelect === 'function') {
    legacyOn.click = (event) => {
      if (typeof originalClick === 'function') {
        originalClick(event)
      }

      onLegacySelect(toggleLegacyItemValue(modelValue, item))
    }
  }

  return {
    ...scope,
    item,
    attrs: {
      ...legacyAttrs,
      inputValue: active
    },
    on: legacyOn,
    active
  }
}

export function normalizeSelectionScope(scope = {}, items, modelValue) {
  const slotItem = normalizeSlotItem(scope?.item)
  const itemValue = getSlotItemValue(slotItem)
  const hasLegacyText =
    slotItem &&
    typeof slotItem === 'object' &&
    (slotItem.text != null || slotItem.title != null)
  const resolvedItem = hasLegacyText
    ? slotItem
    : normalizeSlotItem(
        resolveLegacyItemFromItems(
          items,
          itemValue ?? (Array.isArray(modelValue) ? modelValue[0] : modelValue)
        ) ?? slotItem
      )

  return {
    ...scope,
    item: resolvedItem,
    index: scope?.index ?? scope?.props?.index ?? 0
  }
}

export function shouldRenderLegacySelectionFallback(attrs = {}) {
  return !isMultipleSelect(attrs) && !attrs.chips && !attrs['small-chips']
}

export default defineComponent({
  name: 'VSelectCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,
  emits: [
    'update:modelValue',
    'update:search',
    'update:searchInput',
    'change',
    'input'
  ],

  setup(_, { emit, slots }) {
    const attrs = useAttrs()
    let lastExternalModelValue
    let lastExternalSearchValue
    const localModelValue = ref()
    const localSearchValue = ref()

    return () => {
      const normalizedAttrs = withLegacyDirtyClass(
        stripLegacyAttrs(attrs),
        attrs
      )
      const variant = resolveVariant(attrs)
      const density = resolveDensity(attrs)
      const bgColor = resolveBgColor(attrs)
      const externalModelValue = normalizeModelValue(attrs)
      const externalSearchValue = normalizeSearchValue(attrs)
      if (lastExternalModelValue !== externalModelValue) {
        lastExternalModelValue = externalModelValue
        localModelValue.value = externalModelValue
      } else if (localModelValue.value === undefined) {
        localModelValue.value = externalModelValue
      }
      if (lastExternalSearchValue !== externalSearchValue) {
        lastExternalSearchValue = externalSearchValue
        localSearchValue.value = externalSearchValue
      } else if (localSearchValue.value === undefined) {
        localSearchValue.value = externalSearchValue
      }
      const modelValue = localModelValue.value
      // VSelect.js wraps whatever modelValue it receives with
      // `v === null ? [null] : wrapInArray(v)` to build its internal
      // selection array, and wrapInArray only special-cases null/undefined
      // - an empty string becomes `['']` (length 1), so a freshly-mounted
      // single-select bound to a Vue2-style `''` default (the normal empty
      // value for a v-model'd string) reads as "has a selection" and
      // renders dirty/floating/active before the user ever touches it.
      // Only the value actually handed to the real VSelect needs this - the
      // legacy fallback rendering/selection-matching below still needs the
      // real (possibly '') value to compare item values against.
      const displayModelValue =
        !isMultipleSelect(attrs) && modelValue === '' ? undefined : modelValue
      const items = normalizeItems(attrs.items, attrs)
      // Vuetify 3's real VSelect already renders a custom v-slot:selection
      // through its normal single-select field-input area (VSelect.js maps
      // model.value to '.v-select__selection' divs there, not prepend-inner).
      // The legacy fallback below exists for callers with NO selection slot
      // at all; routing an app-supplied slot through prepend-inner instead
      // squeezes arbitrary-width content into that icon-sized area, wrapping
      // it awkwardly. Only take the fallback path when there's truly no
      // selection slot to pass through.
      const useLegacySelectionFallback =
        !slots.selection &&
        shouldRenderLegacySelectionFallback(attrs) &&
        renderLegacySelectionFallback(slots, items, modelValue) != null
      const legacyTextItems = hasLegacyTextItems(attrs.items)
      const emitLegacyValue = (value) => {
        if (value === localModelValue.value) return
        localModelValue.value = value
        emit('update:modelValue', value)
        emit('change', value)
        emit('input', value)
      }

      delete normalizedAttrs.onInput
      delete normalizedAttrs.onChange
      delete normalizedAttrs['onUpdate:modelValue']
      delete normalizedAttrs['onUpdate:search']
      delete normalizedAttrs['onUpdate:searchInput']

      return h(
        VSelect,
        {
          ...normalizedAttrs,
          ...(variant ? { variant } : {}),
          ...(density ? { density } : {}),
          ...(bgColor ? { bgColor } : {}),
          ...(legacyTextItems && normalizedAttrs.itemTitle == null
            ? { itemTitle: 'text' }
            : {}),
          ...(legacyTextItems && normalizedAttrs.itemValue == null
            ? { itemValue: 'value' }
            : {}),
          items,
          modelValue: displayModelValue,
          search: localSearchValue.value,
          'onUpdate:modelValue': emitLegacyValue,
          'onUpdate:search': (value) => {
            if (value === localSearchValue.value) return
            localSearchValue.value = value
            emit('update:search', value)
            emit('update:searchInput', value)
          }
        },
        {
          ...slots,
          item: slots.item
            ? (scope) =>
                slots.item(
                  normalizeItemScope(scope, modelValue, emitLegacyValue)
                )
            : (scope) => {
                // Vuetify 4's own uncompatted VSelect auto-prepends a
                // checkbox to each item when `multiple` is set (see
                // VSelect.js's native item fallback). This compat layer's
                // item slot always intercepts that fallback, so callers
                // relying on the auto-checkbox (any `multiple` legacy
                // select with no custom v-slot:item) silently lost it.
                // Replicate it here so multi-select items keep their
                // checkbox without every call site having to draw its own.
                const prependSlot = hasBooleanishAttr(attrs.multiple)
                  ? {
                      prepend: ({ isSelected }) =>
                        h(VCheckboxBtn, {
                          modelValue: isSelected,
                          ripple: false,
                          tabindex: -1,
                          'aria-hidden': true,
                          onClick: (event) => event.preventDefault()
                        })
                    }
                  : {}

                return scope.props?.title != null
                  ? h(VListItem, scope.props, prependSlot)
                  : h(VListItem, scope.props, {
                      ...prependSlot,
                      default: () =>
                        renderDefaultItemLabel(normalizeSlotItem(scope.item))
                    })
              },
          selection:
            slots.selection || useLegacySelectionFallback
              ? useLegacySelectionFallback
                ? () => []
                : (scope) =>
                    slots.selection(
                      normalizeSelectionScope(scope, items, modelValue)
                    )
              : undefined,
          'prepend-inner': useLegacySelectionFallback
            ? (...slotArgs) => [
                renderLegacySelectionFallback(slots, items, modelValue),
                slots['prepend-inner']?.(...slotArgs)
              ]
            : slots['prepend-inner']
        }
      )
    }
  }
})
